// src/components/games/pipsWordPuzzle/puzzles.ts
import { logger } from '../../../utils/logger';

import { DailyPuzzle } from './types';
import { supabase } from '../../../services/supabase';

// Cache for today's puzzle
let cachedPuzzle: DailyPuzzle | null = null;
let cacheDate: string | null = null;

// Cache for archive puzzles
let cachedArchive: DailyPuzzle[] = [];

export async function getTodaysPuzzle(): Promise<DailyPuzzle> {
  const today = getDateString();

  // Return cached puzzle if it's still today
  if (cachedPuzzle && cacheDate === today) {
    return cachedPuzzle;
  }

  try {
    // Fetch today's puzzle directly by date_used to ensure everyone gets the same one
    const { data, error } = await supabase
      .from('pips_word_puzzle_puzzles')
      .select('*')
      .eq('date_used', today)
      .single();

    if (error) {
      logger.error('Error fetching puzzle:', error);
      // If no puzzle for today, get the most recent past puzzle as fallback
      return await getMostRecentPuzzle();
    }

    if (!data) {
      logger.warn('No puzzle found for today');
      return await getMostRecentPuzzle();
    }

    // Transform database format to app format
    const dailyPuzzle: DailyPuzzle = {
      word: data.word,
      date: today, // Use today's date for the app
      category: data.category,
      hints: [
        { id: 1, text: data.hint1, cost: 5, revealed: false },
        { id: 2, text: data.hint2, cost: 5, revealed: false },
        { id: 3, text: data.hint3, cost: 5, revealed: false }
      ]
    };

    // Cache the puzzle
    cachedPuzzle = dailyPuzzle;
    cacheDate = today;

    return dailyPuzzle;
  } catch (err) {
    logger.error('Exception fetching puzzle:', err);
    return await getMostRecentPuzzle();
  }
}

// Fallback: Get the most recent puzzle (today or most recent past puzzle)
async function getMostRecentPuzzle(): Promise<DailyPuzzle> {
  try {
    const today = getDateString();

    const { data, error } = await supabase
      .from('pips_word_puzzle_puzzles')
      .select('*')
      .lte('date_used', today)
      .order('date_used', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      logger.error('Error fetching most recent puzzle:', error);
      return getFallbackPuzzle();
    }

    const dailyPuzzle: DailyPuzzle = {
      word: data.word,
      date: today, // Use today's date for the app
      category: data.category,
      hints: [
        { id: 1, text: data.hint1, cost: 5, revealed: false },
        { id: 2, text: data.hint2, cost: 5, revealed: false },
        { id: 3, text: data.hint3, cost: 5, revealed: false }
      ]
    };

    // Cache the puzzle
    cachedPuzzle = dailyPuzzle;
    cacheDate = today;

    return dailyPuzzle;
  } catch (err) {
    logger.error('Exception in fallback:', err);
    return getFallbackPuzzle();
  }
}

export async function getArchivePuzzles(): Promise<DailyPuzzle[]> {
  // Return cached archive if available
  if (cachedArchive.length > 0) {
    return cachedArchive;
  }

  try {
    // Fetch all puzzles from database, ordered by date_used descending
    const { data, error } = await supabase
      .from('pips_word_puzzle_puzzles')
      .select('*')
      .order('date_used', { ascending: false });

    if (error) {
      logger.error('Error fetching archive:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    const archivePuzzles: DailyPuzzle[] = data.map(puzzle => ({
      word: puzzle.word,
      date: puzzle.date_used || getDateString(), // Use date_used from database
      category: puzzle.category,
      hints: [
        { id: 1, text: puzzle.hint1, cost: 5, revealed: false },
        { id: 2, text: puzzle.hint2, cost: 5, revealed: false },
        { id: 3, text: puzzle.hint3, cost: 5, revealed: false }
      ]
    }));

    cachedArchive = archivePuzzles;
    return archivePuzzles;
  } catch (err) {
    logger.error('Exception fetching archive:', err);
    return [];
  }
}

function getFallbackPuzzle(): DailyPuzzle {
  // Fallback puzzles in case database is unavailable
  const fallbacks: DailyPuzzle[] = [
    {
      word: 'QUEST',
      date: getDateString(),
      category: 'RPG Terms',
      hints: [
        { id: 1, text: 'An adventure or mission to accomplish a goal', cost: 5, revealed: false },
        { id: 2, text: 'Often given by NPCs in video games', cost: 5, revealed: false },
        { id: 3, text: 'Can be main or side in RPGs', cost: 5, revealed: false }
      ]
    },
    {
      word: 'MAGIC',
      date: getDateString(),
      category: 'Card Games',
      hints: [
        { id: 1, text: 'The Gathering - a collectible card game', cost: 5, revealed: false },
        { id: 2, text: 'Created by Richard Garfield in 1993', cost: 5, revealed: false },
        { id: 3, text: 'Players are planeswalkers who cast spells', cost: 5, revealed: false }
      ]
    },
    {
      word: 'CHESS',
      date: getDateString(),
      category: 'Classic Games',
      hints: [
        { id: 1, text: 'One of the oldest strategy games in the world', cost: 5, revealed: false },
        { id: 2, text: 'Features pieces like knights, bishops, and rooks', cost: 5, revealed: false },
        { id: 3, text: 'The goal is to checkmate your opponent\'s king', cost: 5, revealed: false }
      ]
    }
  ];

  // Use a deterministic selection based on date
  const dayOfYear = Math.floor((new Date().getTime() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  return fallbacks[dayOfYear % fallbacks.length];
}

export function getDateString(): string {
  // Get UTC date to ensure everyone worldwide gets the same puzzle
  const now = new Date();
  const utcYear = now.getUTCFullYear();
  const utcMonth = String(now.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(now.getUTCDate()).padStart(2, '0');
  return `${utcYear}-${utcMonth}-${utcDay}`;
}